import { DataCollectionFieldModel } from '../../pages/Assessment/types';

// Type for the clean field structure
export interface CleanFieldModel {
  ID: number;
  Guid: string;
  GridRow: number | null;
  GridColumn: number | null;
  Value: string | null;
  Template: {
    ID: number;
    Name: string | null;
    DisplayName: string | null;
    Required: boolean;
    Order: number;
    HelpText: string | null;
    GridChild: boolean;
    FieldAlias: string | null;
    FieldType_ID: number;
    FieldType: {
      ID: number;
      Name: string | null;
      Discriminator: string | null;
    };
    Parameters_ID: number;
    Parameters: {
      ID: number;
      Expression: string | null;
      ValuesListID: number;
      MultiLine: boolean;
      Discriminator: string | null;
      MeasureDetail_ID: number;
      ShowRowsAsColumns: boolean;
      FieldTemplateToMirror_ID: number;
    };
    Validator_ID: number;
    Validator: {
      ID: number;
      MinimumDate: string | null;
      MaximumDate: string | null;
      MinRows: number;
      MaxRows: number;
      AllRowsVisible: boolean;
      Minimum: number;
      Maximum: number;
      IntegerOnly: boolean;
      MinimumLength: number;
      MaximumLength: number;
      Discriminator: string | null;
    };
    OriginalSize: boolean;
    OriginalWidth: number | null;
    OriginalHeight: number | null;
    MaintainAspectRatio: boolean;
    InitiallyVisibleOnLoad: boolean;
    VisibleExternally: boolean;
    Archived: boolean;
    ImageOnly: boolean;
    HasImage: boolean;
    BackgroundColour: string | null;
  };
  GridChildren: Array<Array<CleanFieldModel>>;
  Invalid: boolean;
  FieldTemplate_ID: number;
  CollectionID: number;
  HasImageData: boolean;
}

/**
 * Creates a clean field structure from a DataCollectionFieldModel
 * @param field - The original field data
 * @param gridChildren - Optional grid children to include
 * @param updatedValue - Optional updated value to use instead of field.Value
 * @returns Clean field structure
 */
export const createCleanField = (
  field: DataCollectionFieldModel,
  gridChildren?: Array<Array<CleanFieldModel>>,
  updatedValue?: string | null
): CleanFieldModel => {
  return {
    ID: field.ID,
    Guid: field.Guid,
    GridRow: field.GridRow,
    GridColumn: field.GridColumn,
    Value: updatedValue !== undefined ? updatedValue : field.Value,
    Template: {
      ID: field.Template.ID,
      Name: field.Template.Name,
      DisplayName: field.Template.DisplayName,
      Required: field.Template.Required,
      Order: field.Template.Order,
      HelpText: field.Template.HelpText,
      GridChild: field.Template.GridChild,
      FieldAlias: field.Template.FieldAlias,
      FieldType_ID: field.Template.FieldType_ID,
      FieldType: {
        ID: field.Template.FieldType.ID,
        Name: field.Template.FieldType.Name,
        Discriminator: field.Template.FieldType.Discriminator
      },
      Parameters_ID: field.Template.Parameters_ID,
      Parameters: field.Template.Parameters || {
        ID: field.Template.Parameters_ID,
        Expression: null,
        ValuesListID: 0,
        MultiLine: false,
        Discriminator: null,
        MeasureDetail_ID: 0,
        ShowRowsAsColumns: false,
        FieldTemplateToMirror_ID: 0
      },
      Validator_ID: field.Template.Validator_ID,
      Validator: field.Template.Validator || {
        ID: field.Template.Validator_ID,
        MinimumDate: null,
        MaximumDate: null,
        MinRows: 0,
        MaxRows: 0,
        AllRowsVisible: false,
        Minimum: 0,
        Maximum: 0,
        IntegerOnly: false,
        MinimumLength: 0,
        MaximumLength: 0,
        Discriminator: null
      },
      OriginalSize: field.Template.OriginalSize,
      OriginalWidth: field.Template.OriginalWidth,
      OriginalHeight: field.Template.OriginalHeight,
      MaintainAspectRatio: field.Template.MaintainAspectRatio,
      InitiallyVisibleOnLoad: field.Template.InitiallyVisibleOnLoad,
      VisibleExternally: field.Template.VisibleExternally,
      Archived: field.Template.Archived,
      ImageOnly: field.Template.ImageOnly,
      HasImage: field.Template.HasImage,
      BackgroundColour: field.Template.BackgroundColour
    },
    GridChildren: gridChildren || [],
    Invalid: field.Invalid,
    FieldTemplate_ID: field.Template.ID,
    CollectionID: 0,
    HasImageData: field.Template.HasImage || false
  };
};

/**
 * Creates a clean grid field structure with processed grid children
 * @param field - The original grid field data
 * @param filteredGridChildren - The filtered grid children to include
 * @returns Clean grid field structure
 */
export const createCleanGridField = (
  field: DataCollectionFieldModel,
  filteredGridChildren: Array<Array<DataCollectionFieldModel>>
): CleanFieldModel => {
  const processedGridChildren = filteredGridChildren.map(row => 
    row.map(gridField => createCleanField(gridField))
  );

  return createCleanField(field, processedGridChildren);
}; 